import React, {
  useState, useEffect, useMemo, useContext,
} from "react";
import { toast } from "react-toastify";
import {
  Modal,
  Button,
  Input,
  Form,
  Checkbox,
} from "antd";
import { getUser } from "../../../../../utils/helpers";
import { PendingTableCellContext } from "../../ChemBioPendingContext";
import { checkReadyReports } from "../../../../../actions/sendReports";

const EditResultModal = () => {
  const {
    showEditResultModal,
    modalTest,
    handleCloseModal,
    helperFn,
    mrlInstruments,
  } = useContext(PendingTableCellContext);

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const [loading, setLoading] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const sections = useMemo(() => {
    const list = [
      { label: "Test Name", key: "test" },
      { label: "Genista ID", key: "genista_id" },
    ];
    if (modalTest?.isMRLTest) {
      list.push({ label: "Instrument", key: "loaded_on_instruments" });
    } else {
      list.push({ label: "Result", key: "result" });
    }
    return list;
  }, [modalTest]);
  const [modalTitle, setModalTitle] = useState("");

  useEffect(() => {
    if (modalTest?.isMRLTest) {
      setModalTitle(`Loaded for ${modalTest.genista_id}?`);
    } else {
      setModalTitle("Edit Test Result");
    }
  }, [modalTest]);

  useEffect(() => {
    if (watchValue !== undefined) {
      form
        .validateFields(modalTest?.isMRLTest ? ["loaded_on_instruments"] : ["result"])
        .then(() => {
          if (modalTest?.isMRLTest && (modalTest?.loaded_on_instruments?.length === form.getFieldValue("loaded_on_instruments")?.length)) {
            setSubmittable(false);
          } else {
            setSubmittable(true);
          }
        })
        .catch(() => {
          setSubmittable(false);
        });
    } else if (watchValue === undefined) {
      setSubmittable(false);
    }
  }, [form, watchValue, modalTest]);

  const onModalClose = (refresh) => {
    if (loading && !refresh) return;
    handleCloseModal(refresh);
  };

  const onAfterClose = () => {
    form.resetFields();
    setLoading(false);
    setSubmittable(false);
  };

  const handleUpdateTestStatus = async (data, editResultMessage) => {
    const { test_in_sample_id, loaded_on_instruments } = data;
    const markRunPayload = {
      test_in_sample_details: [
        { id: test_in_sample_id, loaded_on_instruments },
      ],
      action: "analyze",
      user_id: getUser().id,
    };
    const { success, message } = await helperFn.markTestRunOnInstrument(markRunPayload);
    if (success) {
      handleCloseModal(true, editResultMessage);
    } else {
      setLoading(false);
      handleCloseModal(true, editResultMessage);
      toast.error(message);
    }
  };

  const handleUpdateInstrument = async (data) => {
    const { test_in_sample_id } = data;
    const loaded_on_instruments = form.getFieldValue("loaded_on_instruments");
    const markRunPayload = {
      test_in_sample_details: [
        { id: test_in_sample_id, loaded_on_instruments },
      ],
      action: "load",
      user_id: getUser().id,
    };
    const { success, message } = await helperFn.markTestRunOnInstrument(markRunPayload);
    if (success) {
      handleCloseModal(true, message);
    } else {
      setLoading(false);
      handleCloseModal(true, message);
      toast.error(message);
    }
  };

  const handleSaveResult = async () => {
    setLoading(true);
    const formValue = await form.validateFields();
    const {
      genista_id: GenistaID, order_id: OrderID, test, test_in_sample_id, sample_status, dilution_factors_list, loaded_on_instruments,
    } = modalTest;
    const updateData = { test_in_sample_id, dilution_factors_list, loaded_on_instruments };
    if (modalTest?.isMRLTest) {
      handleUpdateInstrument(updateData);
      return;
    }
    const editPayload = {
      GenistaID,
      Analysis: test,
      OrderID,
      Result: formValue.result,
      action: "Save",
      source: "Automation - Chemistry - Pending",
    };
    const { success, message } = await helperFn.updateTestResult(editPayload);
    if (success) {
      checkReadyReports();
      if (sample_status === "analyze") {
        onModalClose(true);
      } else {
        handleUpdateTestStatus(updateData, message);
      }
    } else {
      setLoading(false);
      toast.error(message);
    }
  };

  return (
    <Modal
      centered
      closable
      destroyOnClose
      maskClosable={false}
      title={modalTitle}
      open={showEditResultModal}
      onCancel={() => onModalClose(false)}
      afterClose={onAfterClose}
      width="350px"
      footer={[
        <Button key="submit" type="primary" disabled={!submittable} loading={loading} onClick={handleSaveResult}>
          {modalTest?.isMRLTest ? "Confirm" : "Save result"}
        </Button>,
      ]}
    >
      <Form form={form} name="editResultForm">
        {sections.map((item, idx) => {
          const { label, key } = item;
          let valueNode = (<span>{modalTest?.[item.key]}</span>);
          let rules = [];
          if (key === "result") {
            valueNode = (<Input disabled={loading} />);
            rules = [
              {
                required: true,
                message: "Result cannot be empty",
              },
              { max: 2000, message: "Max length is 2000 characters." },
            ];
          } else if (key === "loaded_on_instruments") {
            valueNode = (
              <Checkbox.Group>
                {mrlInstruments.map((instrumentItem, instrumentIdx) => (
                  <Checkbox
                    key={instrumentIdx}
                    value={instrumentItem.id}
                    disabled={modalTest?.[item.key].includes(instrumentItem.id) || loading}
                  >
                    {instrumentItem.instrument_name}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            );
            rules = [
              {
                required: true,
                message: "Please select an instrument",
              },
            ];
          }
          return ((
            <div key={idx} className="PendingTable__ResultSection">
              <label>{`${label}:`}</label>
              <Form.Item
                className="PendingTable__ResultFormItem"
                rules={rules}
                name={key}
                initialValue={modalTest?.[item.key]}
              >
                {valueNode}
              </Form.Item>
            </div>
          ));
        })}
      </Form>
    </Modal>
  );
};

export default EditResultModal;
